import { makeAutoObservable } from 'mobx'
import Ajax from '../Ajax'
import { Alert, AlertModal, DisplayValue } from '..'
import QuestionLog from '../components/views/main/QuestionLog'
import { DataSource, Template } from '../models/Interfaces';
import { isValid } from 'date-fns';
import { SelectOption } from '../components/form/fields/SelectField';
import { store } from './Store';


export class QuestionnaireStore {

    mergeOverride: any = {};
    mergedDependants: any[] = [];
    dataSources: DataSource[] = null;
    dataSourceUsage: any[] = [];
    loading: boolean = true;
    loadingQuestionnaire: boolean = true;
    templates: Template[] | null = null;
    questionnaire: any = null;
    questionnaires: any[] | null = null;
    notificationEmailOptions: SelectOption[] = []
    currentQuestionnaireId: string | null = null;
    alert: any = null;

    order: "asc" | "desc" = "desc"
    orderedBy: string = "updatedAt"
    searchString: string = ""
    searchAccountUids: string[] = []
    searchTemplateKeys: string[] = []
    searchNotificationEmails: string[] = []

    constructor() {
        makeAutoObservable(this)
    }

    init = async () => {
        this.getTemplates()
        this.getQuestionnaires()
    }

    loadQuestionnaire = async (caseUid, questionnaireUidArray) => {
        await Ajax.Enquiry.LoadQuestionnaires(caseUid, questionnaireUidArray).catch((response) => {
            Alert({message: response.data?.detail})
        })
    }

    handleDataSourceChangeNotification = (docn) => {
        console.log("DataSourceChangeNotification", docn)
        this.alert?.dismiss()

        if (docn.newSources) {
            console.log("Registering loaded questionnaire")
            this.dataSources = docn.newSources
            this.dataSourceUsage = []
        } else {
            console.log("Clearing loaded questionnaire")
            this.dataSources = null
            this.dataSourceUsage = []
        }
    }

    handleInformationRequestIntercepted = (iri: any) => {

        console.log("InformationRequestIntercepted", iri)
        this.dataSourceUsage.push(iri)

        if (this.alert) {
            this.alert.dismiss()
        }

        this.alert = Alert({
            persist: true,
            color: "success",
            // horizontalAnchor: "center",
            message: `${this.dataSourceUsage.length} answer${this.dataSourceUsage.length === 1 ? "" : "s"} sourced`,
            actions: [{ label: "See answers", color: "info", action: () => AlertModal({id: "answer-feed", body: <QuestionLog />, width: "450px"}) }]
        })
    }

    getQuestionnaire = async (uid) => {
        await Ajax.Questionnaire.Get(uid).then((response) => {
            this.questionnaire = response.data
        }).catch((response) => {
            Alert({message: response.data?.detail ? response.data.detail : "Couldn't get questionnaire"})
        })
    }

    getQuestionnaires = async () => {
        this.loading = true
        
        var notificationEmailOptions: SelectOption[] = [{value: "", label: "Unassigned"}]
        var questionnaires = []

        await Ajax.Questionnaire.All().then((response) => {
            questionnaires = response.data
            response.data.forEach((q) => {
                if (q.notificationEmail && !notificationEmailOptions.includes(q.notificationEmail)) {
                    notificationEmailOptions.push({value: q.notificationEmail, label: q.notificationEmail})
                }
            })
        }).catch(() => {
            Alert({message: "Failed to get questionnaires"})
        })

        if (this.questionnaires == null) { // First load only
            this.notificationEmailOptions = notificationEmailOptions
            this.searchNotificationEmails = notificationEmailOptions.map(o => o.value)
        }

        this.questionnaires = questionnaires

        this.loading = false
    }

    getTemplates = async () => {
        await Ajax.QuestionnaireTemplate.All().then((response) => {
            this.templates = response.data
        }).catch(() => {
            this.templates = []
        })

        this.loading = false
    }

    // #region Filtering

    get templateKeyOptions () {
        var templateKeyOptions: SelectOption[] = []
    
        store.QuestionnaireStore.questionnaires?.forEach(questionnaire => {
            if (!templateKeyOptions.find(t => t.value === questionnaire.template.key)) {
                var label = DisplayValue("template_key", questionnaire.template.key)
                templateKeyOptions.push({value: questionnaire.template.key, label: label ? label : "Other"})   
            }
        })

        templateKeyOptions.sort((a, b) => a.label?.localeCompare(b.label))
        return templateKeyOptions
    }

    

    get advisorOptions () {
        var clientOptions: SelectOption[] = []
        // Add customer accounts
        store.NodeStore.accounts?.filter((a) => !!a.advisorUid && ["customer", "admin"].includes(a.accountType)).forEach((a) => {
            clientOptions.push({value: `${a.advisorUid}-parent-option`, label: a.label, children: [{value: a.advisorUid, label: `${a.label} (Unassigned)`}]})
        })
        // Add sponsor accounts nested
        store.NodeStore.accounts?.filter((a) => !!a.advisorUid && ["sponsor", "client"].includes(a.accountType)).forEach((a) => {
            var parentAccount = store.NodeStore.accounts.find(pa => pa.uid === a.parent.uid)
            var existingParentOption = clientOptions.find(option => option.value === `${parentAccount.advisorUid}-parent-option`)
            if (existingParentOption) {
                existingParentOption.children.push({value: a.advisorUid, label: a.label, children: []})
            } else {
                console.log("Couldn't find parent account option")
            }
        })

        if (clientOptions.length === 1) {
            clientOptions = clientOptions[0].children
        }

        return clientOptions
    }

    setSearchAccountUids = (uidList) => {
        if (uidList === null) {
            this.searchAccountUids = store.NodeStore.accounts.filter(a => !!a.advisorUid).map(a => a.advisorUid)
        } else {
            this.searchAccountUids = uidList
        }
    }

    setSearchTemplateKeys = (keyList) => {
        if (keyList === null) {
            if (this.questionnaires) {
                this.searchTemplateKeys = [...new Set(this.questionnaires.map(q => q.template?.key))]
            }
        } else {
            this.searchTemplateKeys = keyList
        }
    }

    setSearchNotificationEmails = (keyList) => {
        if (keyList === null) {
            if (this.questionnaires) {
                this.searchNotificationEmails = this.notificationEmailOptions.map(o => o.value)
            }
        } else {
            this.searchNotificationEmails = keyList
        }
    }

    setSearchTemplateKeysByAutomationId = (automationId) => {
        var automationQuestionnaireKeys = {
            "VAF_NAT":                      ["NAT"],

            "SMS_COS_APPLY":                ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            "SMS_COS_ASSIGN_D":             ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            "SMS_COS_ASSIGN":               ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            "SMS_CAS_ASSIGN":               ["STU_SPONSOR", "STU_APPLICANT"],

            "VAF_SWV_EC":                   ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            "VAF_SWV_FLR":                  ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            "VAF_SWV_UKVI":                 ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],

            "VAF_STUDENT_EC":               ["STU_SPONSOR", "STU_APPLICANT"],
            "VAF_STUDENT_FLR":              ["STU_SPONSOR", "STU_APPLICANT"],
            "VAF_STUDENT_UKVI":             ["STU_SPONSOR", "STU_APPLICANT"],

            "VAF_STUDENT_CHILD_EC":         ["STU_SPONSOR", "STU_APPLICANT"],
            "VAF_STUDENT_CHILD_FLR":        ["STU_SPONSOR", "STU_APPLICANT"],
            "VAF_STUDENT_CHILD_UKVI":       ["STU_SPONSOR", "STU_APPLICANT"],

            "VAF_GBM_UKVI":                 ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            "VAF_GBM_EXTEND":               ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            "VAF_GBM_EC":                   ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            
            "VAF_DEP_PARTNER_EC":           ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA", "DEP_PARTNER"],
            "VAF_DEP_PARTNER_FLR":          ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA", "DEP_PARTNER"],
            "VAF_DEP_PARTNER_FLR_COMBO":    ["DEP_PARTNER"],
            "VAF_DEP_PARTNER_UKVI":         ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA", "DEP_PARTNER"],
            "VAF_DEP_CHILD_EC":             ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA", "DEP_CHILD"],
            "VAF_DEP_CHILD_FLR":            ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA", "DEP_CHILD"],
            "VAF_DEP_CHILD_FLR_COMBO":      ["DEP_CHILD"],
            "VAF_DEP_CHILD_UKVI":           ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA", "DEP_CHILD"],
        }
        
        var questionnaireKeyList = automationQuestionnaireKeys[automationId]
        
        if (questionnaireKeyList?.length) {
            this.setSearchTemplateKeys(questionnaireKeyList)
        } else {
            this.setSearchTemplateKeys(null)
        }

        return questionnaireKeyList
    }

    setSearchTemplateKeysByStatusId = (statusId) => {
        var statusQuestionnaireKeys = {
            "UKBC"           : ["NAT"],
            "UKCS_SW_J"      : ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            "UKLLR_SW_P"     : ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            "UKLLR_UKX_P"    : ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            "UKLLR_GTR_P"    : ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            "UKLLR_SNR_P"    : ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "SWV_JA"],
            "UKLLR_SWDC_P"   : ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "DEP_CHILD"],
            "UKLLR_SWDP_P"   : ["SWV_SPONSOR", "SWV_SPONSOR_JOB", "DEP_PARTNER"],

        }
        var questionnaireKeyList = statusQuestionnaireKeys[statusId]
        if (questionnaireKeyList?.length) {
            console.log("Setting", questionnaireKeyList)
            this.setSearchTemplateKeys(questionnaireKeyList)
        }

        return questionnaireKeyList
    }

    setOrder = (order) => {
        this.order = order
    }

    setOrderedBy = (orderedBy) => {
        this.orderedBy = orderedBy
    }

    get orderPhrase () {
        var orderPhrase = this.order === "asc" ? ", reverse" : ""

        if (["On", "At"].includes(this.orderedBy.slice(-2))) {
            orderPhrase = this.order === "asc" ? ", oldest first" : ", newest first"
        }

        return `ordered by '${DisplayValue("dict_key", this.orderedBy)}'${orderPhrase}`
    }

    setSearchString = (str) => {
        this.searchString = str
    }

    filterQuestionnaires = (questionnaires) => {

        if (!Array.isArray(questionnaires) || !questionnaires.length){
            return []
        }

        // Filtering
        if (this.searchAccountUids) {
            questionnaires = questionnaires.filter(q => this.searchAccountUids.includes(q.respondent.advisorUid))
        }

        if (this.searchTemplateKeys) {
            questionnaires = questionnaires.filter(q => this.searchTemplateKeys.includes(q.template.key))
        }

        if (this.searchNotificationEmails) {
            questionnaires = questionnaires.filter(q => this.searchNotificationEmails.includes(q.notificationEmail))
        }

        if (this.searchString) {
            questionnaires = questionnaires.filter(q => {
                return (
                    q.uid === this.searchString ||
                    q.displayName?.toLowerCase().includes(this.searchString.toLowerCase()) ||
                    q.template?.displayName?.toLowerCase().includes(this.searchString.toLowerCase()) ||
                    q.respondent?.respondentEmail?.toLowerCase().includes(this.searchString.toLowerCase()) ||
                    q.notificationEmail?.toLowerCase().includes(this.searchString.toLowerCase())
                )
            })
        }

        // Ordering
        questionnaires = [...questionnaires].sort((a, b) => {

            var aValue = null
            var bValue = null

            if (this.orderedBy === "template") {
                aValue = a.template?.displayName
                bValue = b.template?.displayName
            } else if (this.orderedBy === "respondent") {
                aValue = a.respondent.respondentEmail
                bValue = b.respondent.respondentEmail
            } else if (this.orderedBy === "percentageComplete") {
                aValue = a.percentageComplete?.toString()
                bValue = b.percentageComplete?.toString()
            } else if (Object.keys(a).includes(this.orderedBy) && Object.keys(b).includes(this.orderedBy)) {
                aValue = a[this.orderedBy]
                bValue = b[this.orderedBy]
            }

            // Date fields need opposite ordering
            if (["On", "At"].includes(this.orderedBy.slice(-2))) {
                return this.order === "desc" ? bValue?.localeCompare(aValue) : aValue?.localeCompare(bValue)
            }

            return this.order === "desc" ? aValue?.localeCompare(bValue) : bValue?.localeCompare(aValue)
        })

        return questionnaires
    }

    filterQuestionnairesByTags = (tags: any) => {

        var questionnaires = this.questionnaires

        if (typeof tags !== "object" || !Object.keys(tags).length) {
            return []
        }

        questionnaires = store.QuestionnaireStore.questionnaires?.filter(q => {
            if (typeof q.tags !== "object" || !Object.keys(q.tags).length) {
                return false
            }
            var tagNotFound = Object.keys(tags).find(tagName => {
                return tags[tagName] !== q.tags[tagName]
            })
            return !tagNotFound
        })   

        return questionnaires
    }

    // #endregion

    setMergeOverride = (data) => {
        this.mergeOverride = data
    }

    setMergedDependants = (data) => {
        this.mergedDependants = data
    }

    getQuestionnaireList = async (questionnaireUidArray: string[]) => {
        
        var qArray = []
        var promiseArray = []

        // Get questionnaires
        for (var i = 0; i < questionnaireUidArray?.length; i += 1) {
            var promise = Ajax.Questionnaire.Get(questionnaireUidArray[i])
            promiseArray.push(promise)
        }

        for (var i = 0; i < promiseArray.length; i += 1) {
            await promiseArray[i].then(response => {
                response.data.answers = JSON.parse(response.data.answers)
                qArray.push(response.data)
            })
        }

        return qArray
    }

    assignPageIDs = async (templateJson: string): Promise<string> => {

        console.log(templateJson)

        var templateBody: ITemplateBody = JSON.parse(templateJson)

        if (!templateBody.pages || !Array.isArray(templateBody.pages)) return templateJson

        const traverse = async (pages: IPage[]) => {

            for (var p=0; p < pages.length; p+=1) {

                var page: IPage = pages[p]
                page.id = await this.generatePageId(page)

                if (page.fields && Array.isArray(page.fields)) {
                    for (var f=0; f < page.fields.length; f+=1) {

                        var field: IField = page.fields[f]

                        if (field.conditional_pages) {
                            Object.values(field.conditional_pages).forEach((pageList: IPage[]) => {
                                traverse(pageList)
                            })
                        }
                    }
                }
            }
        }

        await traverse(templateBody.pages)

        return JSON.stringify(templateBody, null, "    ")
    }


    generatePageId = async (page: IPage) => {

        // Remove ID and conditional_pages
        var pageToHash = {
            ...page,
            id: null,
            fields: page.fields?.map(field => {
                return {...field, conditional_pages: null}
            })
        }

        const encoder = new TextEncoder();
        const data = encoder.encode(JSON.stringify(pageToHash));
        const hashBuffer = await crypto.subtle.digest("SHA-256", data)
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
        return hashHex
    }
}


// Template components
export interface ITemplateBody {
    name: string;
    pages: IPage[];
}

export interface IPage {
    id: string;
    title: string;
    description?: string;
    table?: ITable;
    fields?: IField[];
    submitted?: boolean;
    dependsOn?: Record<string, string>;
    dependsOnAny?: Record<string, string[]>;
    conditional_skip?: string;
}

export interface IField {
    name: string;
    type: string;
    label?: string;
    options?: Record<string, string>;
    guess?: string;
    regex?: string;
    regexMessage?: string;
    min?: number;
    max?: number;
    evidence_types?: string[];
    allowed_formats?: string[];
    helpText?: string;
    required?: boolean;
    setListKey?: string;
    dependsOn?: Record<string, string>;
    dependsOnAny?: Record<string, string[]>;
    conditional_pages?: Record<string, IPage[]> ;
}

export interface ITable {
    fields: IField[];
    list_name?: string;
    list_item_label?: string;
    min_rows?: number;
    max_rows?: number;
    columns?: Record<string, string>;
}